<!-- eslint-disable vue/no-v-html -->
<template>
    <!-- Current Contract Terms -->
    <VJoyWrapper v-if="currentTerm" row-gap="3" direction="column" align="flex-start">
        <template v-if="currentTerm.type === 'TRIPARTITE'">
            <VJoyHighlight level="success" display-icon>
                <span
                    v-html="
                        $t('projectclient.projectview.details.terms.project.terms.client.accepted', [
                            currentTerm.url,
                            formatDate(currentTerm.clientAcceptedAt),
                        ])
                    "
                />
            </VJoyHighlight>

            <VJoyHighlight level="success" display-icon>
                <span
                    v-html="
                        $t('projectclient.projectview.details.terms.project.terms.freelancer.accepted', [
                            escapedFreelancerFirstName,
                            currentTerm.url,
                            formatDate(currentTerm.freelancerAcceptedAt),
                        ])
                    "
                />
            </VJoyHighlight>
        </template>

        <template v-else-if="currentTerm.type === 'SUBCONTRACTING'">
            <VJoyText preset="primary-450">
                {{ $t('projectclient.projectview.details.terms.client.terms.title') }}
            </VJoyText>

            <VJoyHighlight level="success" display-icon>
                <span
                    v-html="
                        $t('projectclient.projectview.details.terms.client.terms.title.freelancer.accepted', [
                            escapedFreelancerFirstName,
                            toFinalUrl(currentTerm.url),
                            formatDate(currentTerm.freelancerAcceptedAt),
                        ])
                    "
                />
            </VJoyHighlight>
        </template>
    </VJoyWrapper>

    <!-- Archived Contract Terms -->
    <VJoyWrapper v-if="archivedClientTerms.length > 0 || archivedProjectTerms.length > 0" row-gap="3" direction="column" align="flex-start">
        <VJoyText preset="primary-350">
            {{ $t('projectclient.projectview.details.terms.project.terms.archived') }}
        </VJoyText>

        <VJoyText v-for="(term, index) in archivedProjectTerms" :key="index" preset="primary-300">
            <span
                v-html="$t('projectclient.projectview.details.terms.project.terms.client.accepted', [term.url, formatDate(term.clientAcceptedAt)])"
            />
        </VJoyText>

        <VJoyText v-for="(term, index) in archivedClientTerms" :key="index" preset="primary-300">
            <span
                v-html="
                    $t('projectclient.projectview.details.terms.client.terms.title.freelancer.accepted', [
                        escapedFreelancerFirstName,
                        toFinalUrl(term.url),
                        formatDate(term.freelancerAcceptedAt),
                    ])
                "
            />
        </VJoyText>
    </VJoyWrapper>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useProjectInformationStore} from '@common/stores/project.store';
    import {storeToRefs} from 'pinia';
    import {useFormat} from '#imports';
    import {escapeHtml} from '@malt/sanitize';
    import {VJoyWrapper, VJoyText, VJoyHighlight} from '@maltjoy/core-vue';
    import type {InvoicingMode} from '@api';

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation} = storeToRefs(projectInformationStore);

    const {formatDate} = useFormat();

    export interface TermHistoryItem {
        type: InvoicingMode;
        url: string;
        freelancerAcceptedAt: Date;
        clientAcceptedAt?: Date;
        isCurrent: boolean;
    }

    const props = defineProps<{
        terms: TermHistoryItem[];
    }>();

    const escapedFreelancerFirstName = computed(() => escapeHtml(projectInformation.value.freelancer.firstName));

    const currentTerm = computed(() => props.terms.find((t) => t.isCurrent));
    const archivedProjectTerms = computed(() => props.terms.filter((t) => t.type === 'TRIPARTITE' && !t.isCurrent));
    const archivedClientTerms = computed(() => props.terms.filter((t) => t.type === 'SUBCONTRACTING' && !t.isCurrent));

    // FDE : Temporay frontend fix before fixing amendments in DB
    function toFinalUrl(filename: string): string {
        return filename.toLowerCase().endsWith('.pdf') ? filename : `${filename}.pdf`;
    }
</script>
